import {
  createDefaultAutomation as createTrackerAutomationSettings,
  normalizeAutomationEvents,
} from "../trackerStorage";
import { getQuestionLibrary } from "../questions/questionStorage";
import {
  addTrackerAutomationApi,
  deleteTrackerAutomationApi,
  getTrackerAutomationDetailApi,
  getTrackerAutomationsApi,
  updateTrackerAutomationApi,
} from "../../../DAL/trackerAutomation/trackerAutomation";

/** Trigger models from GymRite docs — events-first, not Power Automate–locked. */
export const AUTOMATION_TRIGGER_MODELS = [
  {
    value: "event",
    title: "Event-based",
    description:
      "Fire when something happens — submission, status change, review due / overdue.",
  },
  {
    value: "relative",
    title: "Individual / Relative",
    description:
      "Based on the individual's own Journey — Day 7, X days before review, no response for X days.",
  },
  {
    value: "calendar",
    title: "Fixed Calendar / Cohort",
    description:
      "Based on the programme calendar. Late joiners enter the current eligible window only.",
  },
];

export const AUTOMATION_TRIGGERS = [
  {
    id: "new_submission",
    label: "New Submission",
    description: "A person submits on this journey.",
    models: ["event"],
  },
  {
    id: "submission_updated",
    label: "Submission Updated",
    description: "An existing journey entry is updated.",
    models: ["event"],
  },
  {
    id: "submission_pending",
    label: "Submission Pending",
    description:
      "A member's check-in is due and they have not submitted it yet.",
    models: ["event"],
  },
  {
    id: "coach_general_review",
    label: "General submission review",
    description: "Before / after the coach's check-in review is due.",
    models: ["event"],
  },
  {
    id: "coach_scheduled_review",
    label: "Scheduled submission review",
    description: "Before / after a scheduled coach review is due.",
    models: ["event"],
  },
  {
    id: "review_due",
    label: "Review Due",
    description: "A scheduled review / submission checkpoint becomes due.",
    models: ["event"],
  },
  {
    id: "review_overdue",
    label: "Review Overdue",
    description: "A due review has passed without completion.",
    models: ["event"],
  },
  {
    id: "follow_up_outstanding",
    label: "Follow-up Outstanding",
    description: "A follow-up action is waiting on the person or coach.",
    models: ["event"],
  },
  {
    id: "follow_up_completed",
    label: "Follow-up Completed",
    description: "A follow-up workflow has been completed.",
    models: ["event"],
  },
  {
    id: "status_changed",
    label: "Status Changed",
    description: "A coach or admin moves the person to another status.",
    models: ["event"],
  },
  {
    id: "tags_changed",
    label: "Tags Changed",
    description: "Tags on the person's journey entry are added, removed, or set.",
    models: ["event"],
  },
  {
    id: "member_left",
    label: "Member left journey",
    description: "Fires when a member leaves the journey from their portal.",
    models: ["event"],
  },
  {
    id: "milestone_achieved",
    label: "Milestone Achieved",
    description: "A measurable or journey milestone is reached.",
    models: ["event"],
  },
  {
    id: "file_uploaded",
    label: "File Uploaded",
    description: "A file is uploaded on an entry.",
    models: ["event"],
  },
  {
    id: "image_uploaded",
    label: "Image Uploaded",
    description: "An image is uploaded on an entry.",
    models: ["event"],
  },
  {
    id: "week_completion",
    label: "Week Completion (legacy)",
    description:
      "Legacy trigger — use New Submission for check-in reports. Still runs if already saved.",
    models: ["event"],
    builderOnly: true,
    legacy: true,
  },
  {
    // Individual-member automations (general-notification style): no journey
    // event trigger — emails go to the chosen main members.
    id: "audience_message",
    label: "Audience message",
    description:
      "Email the main members chosen on this automation (all / journey / specific).",
    models: ["event"],
    builderOnly: true,
  },
  {
    id: "relative_offset",
    label: "Relative checkpoint",
    description:
      "N days / weeks / months after journey start, last submit, or last review.",
    models: ["relative"],
  },
  {
    id: "calendar_date",
    label: "Calendar date",
    description:
      "Fixed month/day each year. Mid-cycle joiners enter the next scheduled cycle.",
    models: ["calendar"],
  },
];

export const AUTOMATION_ACTIONS = [
  {
    value: "email",
    label: "Send email",
    description: "GymRite writes and sends the message.",
  },
  {
    value: "record_event",
    label: "Record the event only",
    description:
      "No mail from GymRite. The event still exists if Power Automate is plugged in later.",
  },
  {
    value: "notify",
    label: "In-app notification",
    description: "Ping in the portal.",
  },
  {
    value: "create_coach_review",
    label: "Create coach review",
    description: "Queue a coach review for the entry.",
  },
  {
    value: "assign_status",
    label: "Assign status",
    description: "Move the person to a status on this tracker.",
  },
  {
    value: "assign_tags",
    label: "Assign tags",
    description: "Add, remove, or set tags from this journey's vocabulary.",
  },
];

export const TAG_MODE_OPTIONS = [
  { value: "add", label: "Add tags" },
  { value: "remove", label: "Remove tags" },
  { value: "set", label: "Replace all tags" },
];

export const AUTOMATION_RECIPIENTS = [
  { value: "person", label: "Person / Member" },
  { value: "coach", label: "Assigned coach" },
  { value: "both", label: "Person and coach" },
  { value: "admins", label: "Admins" },
];

export const RELATIVE_FROM_OPTIONS = [
  { value: "journey_start", label: "Journey start" },
  { value: "last_submission", label: "Last member submission" },
  { value: "last_coach_review", label: "Last coach review" },
];

export const RELATIVE_UNITS = [
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
  { value: "months", label: "Months" },
];

export const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

/**
 * Standard dynamic content from GymRite docs (Email Template Builder).
 * Custom Journey Responses are appended per tracker.
 */
export const STANDARD_DYNAMIC_FIELDS = [
  { token: "first_name", label: "First Name", group: "Standard" },
  { token: "full_name", label: "Full Name", group: "Standard" },
  { token: "email", label: "Email", group: "Standard" },
  { token: "coach_name", label: "Coach", group: "Standard" },
  { token: "location", label: "Location", group: "Standard" },
  { token: "journey_name", label: "Journey Name", group: "Standard" },
  { token: "start_date", label: "Start Date", group: "Standard" },
  {
    token: "current_week_stage",
    label: "Current Week/Stage",
    group: "Standard",
  },
  { token: "status", label: "Status", group: "Standard" },
  { token: "review_date", label: "Review Date", group: "Standard" },
  { token: "follow_up_date", label: "Follow-up Date", group: "Standard" },
];

const now = () => new Date().toISOString();

const slugToken = (label, fallback) => {
  const base = String(label || fallback || "field")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 48);
  return base || fallback || "field";
};

export const toDynamicToken = (token) => `{{${token}}}`;

const LEGACY_TRIGGER_IDS = {
  on_first_submission: "new_submission",
  on_follow_up_submission: "submission_updated",
  member_review_due: "review_due",
  member_review_overdue: "review_overdue",
  coach_review_due: "review_due",
  coach_review_overdue: "review_overdue",
  journey_completed: "milestone_achieved",
  unassigned_coach: "follow_up_outstanding",
};

export const normalizeAutomationTriggerId = (id) =>
  LEGACY_TRIGGER_IDS[id] || id;

export const createEmptyWorkflow = () => ({
  id: `wf_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
  name: "",
  description: "",
  status: "active",
  triggerModel: "event",
  triggerId: "new_submission",
  relativeAmount: 7,
  relativeUnit: "days",
  relativeFrom: "journey_start",
  calendarMonth: 1,
  calendarDay: 1,
  action: "email",
  recipients: "both",
  subject: "",
  bodyFormat: "html",
  message:
    "<p>Hi {{first_name}},</p><p>This is a reminder about {{journey_name}}.</p><p>Thanks,<br />{{coach_name}}</p>",
  attachments: [],
  assignStatusName: "",
  assignTags: [],
  tagMode: "add",
  requireAnyTag: [],
  createdAt: now(),
  updatedAt: now(),
});

export const AUTOMATION_SCOPE_TYPES = {
  TRACKER: "tracker",
  TAGS: "tags",
  // Audience like general notifications: all / by journey / specific members.
  INDIVIDUAL: "individual",
};

const INDIVIDUAL_AUDIENCE_TYPES = ["all", "tracker", "members"];

/** Saved individual audience (scopeMember), or null. */
const normalizeScopeMember = (member) => {
  if (!member || typeof member !== "object") return null;
  let audienceType = String(member.audienceType || "").toLowerCase();
  if (!INDIVIDUAL_AUDIENCE_TYPES.includes(audienceType)) {
    const legacy = {
      personKey: String(member.personKey || ""),
      memberId: String(member.memberId || ""),
      email: String(member.email || "").trim().toLowerCase(),
      name: String(member.name || ""),
    };
    if (!legacy.personKey && !legacy.memberId && !legacy.email) return null;
    return {
      audienceType: "members",
      members: [legacy],
      trackerStatus: "active",
      ...legacy,
    };
  }
  if (audienceType === "all") {
    return { audienceType: "all", members: [], trackerStatus: "active" };
  }
  if (audienceType === "tracker") {
    const trackerStatus = ["active", "left", "all"].includes(
      String(member.trackerStatus || "").toLowerCase()
    )
      ? String(member.trackerStatus).toLowerCase()
      : "active";
    return { audienceType: "tracker", members: [], trackerStatus };
  }
  const fromList = (Array.isArray(member.members) ? member.members : [])
    .map((m) => ({
      personKey: String(m?.personKey || ""),
      memberId: String(m?.memberId || m?._id || ""),
      email: String(m?.email || "").trim().toLowerCase(),
      name: String(m?.name || ""),
    }))
    .filter((m) => m.personKey || m.memberId || m.email);
  if (!fromList.length) {
    const one = {
      personKey: String(member.personKey || ""),
      memberId: String(member.memberId || ""),
      email: String(member.email || "").trim().toLowerCase(),
      name: String(member.name || ""),
    };
    if (one.personKey || one.memberId || one.email) fromList.push(one);
  }
  if (!fromList.length) return null;
  const first = fromList[0];
  return {
    audienceType: "members",
    members: fromList,
    trackerStatus: "active",
    personKey: first.personKey,
    memberId: first.memberId,
    email: first.email,
    name:
      fromList.length === 1 ? first.name : `${fromList.length} members`,
  };
};

export const describeIndividualScope = (automation) => {
  const scope = normalizeScopeMember(automation?.scopeMember);
  if (!scope) return "Individual member";
  if (scope.audienceType === "all") return "All members";
  if (scope.audienceType === "tracker") {
    const status =
      scope.trackerStatus === "left"
        ? "left"
        : scope.trackerStatus === "all"
          ? "everyone"
          : "active";
    return `Journey · ${automation?.trackerName || "—"} (${status})`;
  }
  return scope.name || "Specific members";
};

export const createEmptyAutomation = (tracker = null) => ({
  id: `automation_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
  scopeType: AUTOMATION_SCOPE_TYPES.TRACKER,
  scopeTags: [],
  scopeMember: null,
  trackerId: tracker?.id || "",
  trackerName: tracker?.name || "",
  name: tracker?.name
    ? `${tracker.name} Automation`
    : "Journey Automation",
  description: "",
  enabled: true,
  status: "active",
  recipients: "both",
  notifyUnassignedCoach: true,
  events: createTrackerAutomationSettings().events,
  workflows: [],
  createdAt: now(),
  updatedAt: now(),
});

/** @deprecated use createEmptyWorkflow — kept for older imports */
export const createEmptyAutomationWorkflow = createEmptyWorkflow;

export const getTriggerMeta = (triggerId) =>
  AUTOMATION_TRIGGERS.find((item) => item.id === triggerId) || null;

export const getTriggerLabel = (triggerId) =>
  getTriggerMeta(triggerId)?.label || triggerId || "—";

export const getTriggerModelLabel = (value) =>
  AUTOMATION_TRIGGER_MODELS.find((item) => item.value === value)?.title ||
  value ||
  "—";

export const getActionLabel = (value) =>
  AUTOMATION_ACTIONS.find((item) => item.value === value)?.label || value || "—";

export const getRecipientLabel = (value) =>
  AUTOMATION_RECIPIENTS.find((item) => item.value === value)?.label ||
  value ||
  "—";

export const getTriggersForModel = (model) =>
  AUTOMATION_TRIGGERS.filter(
    (item) => item.models.includes(model) && !item.builderOnly
  );

export const describeWorkflowTiming = (workflow) => {
  if (!workflow) return "—";
  if (workflow.triggerModel === "relative") {
    const from =
      RELATIVE_FROM_OPTIONS.find((o) => o.value === workflow.relativeFrom)
        ?.label || "start";
    return `${workflow.relativeAmount || 0} ${
      workflow.relativeUnit || "days"
    } after ${from}`;
  }
  if (workflow.triggerModel === "calendar") {
    const month =
      MONTH_OPTIONS.find((o) => o.value === Number(workflow.calendarMonth))
        ?.label || "—";
    return `Every ${month} ${workflow.calendarDay || 1}`;
  }
  return getTriggerLabel(workflow.triggerId);
};

/** @deprecated alias */
export const describeAutomationTiming = describeWorkflowTiming;

export const normalizeWorkflow = (workflow = {}) => {
  const base = { ...createEmptyWorkflow(), ...workflow };
  const triggerId = normalizeAutomationTriggerId(
    base.triggerId || "new_submission"
  );
  const known = AUTOMATION_TRIGGERS.some((item) => item.id === triggerId);
  return {
    ...base,
    id: base.id || createEmptyWorkflow().id,
    triggerId: known ? triggerId : "new_submission",
    name: base.name || "",
    status: base.status === "draft" ? "draft" : "active",
    attachments: Array.isArray(base.attachments) ? base.attachments : [],
    bodyFormat:
      base.action === "email"
        ? base.bodyFormat || "html"
        : base.bodyFormat || undefined,
    assignTags: Array.isArray(base.assignTags) ? base.assignTags : [],
    tagMode: ["add", "remove", "set"].includes(base.tagMode)
      ? base.tagMode
      : "add",
    requireAnyTag: Array.isArray(base.requireAnyTag) ? base.requireAnyTag : [],
  };
};

export const normalizeAutomation = (automation = {}, tracker = null) => {
  const id = String(automation.id || automation._id || "");
  const trackerId = String(automation.trackerId || "");
  const defaults = createEmptyAutomation(tracker);
  const scopeType =
    automation.scopeType === AUTOMATION_SCOPE_TYPES.TAGS
      ? AUTOMATION_SCOPE_TYPES.TAGS
      : automation.scopeType === AUTOMATION_SCOPE_TYPES.INDIVIDUAL
      ? AUTOMATION_SCOPE_TYPES.INDIVIDUAL
      : AUTOMATION_SCOPE_TYPES.TRACKER;
  return {
    ...defaults,
    ...automation,
    id: id || defaults.id,
    scopeType,
    scopeMember: normalizeScopeMember(automation.scopeMember),
    scopeTags: Array.isArray(automation.scopeTags)
      ? automation.scopeTags.filter(Boolean)
      : [],
    trackerId,
    trackerName:
      tracker?.name || automation.trackerName || defaults.trackerName,
    name: automation.name || defaults.name,
    enabled: automation.enabled !== false,
    status: automation.status === "draft" ? "draft" : "active",
    recipients: automation.recipients || "both",
    notifyUnassignedCoach: automation.notifyUnassignedCoach !== false,
    events: normalizeAutomationEvents(automation.events),
    workflows: Array.isArray(automation.workflows)
      ? automation.workflows.map(normalizeWorkflow)
      : [],
  };
};

/** Payload for create/update API (no client-only fields). */
export const toAutomationApiPayload = (automation = {}) => {
  const shaped = normalizeAutomation(automation);
  const scopeType = shaped.scopeType;
  const payload = {
    scopeType,
    scopeTags:
      scopeType === AUTOMATION_SCOPE_TYPES.TAGS
        ? Array.isArray(shaped.scopeTags)
          ? shaped.scopeTags.filter(Boolean)
          : []
        : [],
    name: String(shaped.name || "").trim(),
    description: shaped.description || "",
    enabled: shaped.enabled !== false,
    status: shaped.enabled !== false ? "active" : "draft",
    recipients: shaped.recipients || "both",
    notifyUnassignedCoach: shaped.notifyUnassignedCoach !== false,
    events: shaped.events || [],
    workflows: (shaped.workflows || []).map((wf) => ({
      ...wf,
      id: wf.id,
      name: wf.name || "",
      description: wf.description || "",
      status: wf.status === "draft" ? "draft" : "active",
      triggerModel: wf.triggerModel || "event",
      triggerId: wf.triggerId || "new_submission",
      relativeAmount: Number(wf.relativeAmount) || 0,
      relativeUnit: wf.relativeUnit || "days",
      relativeFrom: wf.relativeFrom || "journey_start",
      calendarMonth: Number(wf.calendarMonth) || 1,
      calendarDay: Number(wf.calendarDay) || 1,
      action: wf.action || "email",
      recipients: wf.recipients || "both",
      subject: wf.subject || "",
      message: wf.message || "",
      bodyFormat: wf.bodyFormat || (wf.action === "email" ? "html" : undefined),
      attachments: Array.isArray(wf.attachments) ? wf.attachments : [],
      assignStatusName: wf.assignStatusName || "",
      assignTags: Array.isArray(wf.assignTags) ? wf.assignTags : [],
      tagMode: ["add", "remove", "set"].includes(wf.tagMode)
        ? wf.tagMode
        : "add",
      requireAnyTag: Array.isArray(wf.requireAnyTag) ? wf.requireAnyTag : [],
      createdAt: wf.createdAt || now(),
      updatedAt: wf.updatedAt || now(),
    })),
  };

  // Tag-scoped automations must omit trackerId — empty string fails Joi.
  if (scopeType === AUTOMATION_SCOPE_TYPES.TRACKER) {
    payload.trackerId = String(shaped.trackerId || "");
    payload.trackerName = shaped.trackerName || "";
  }
  // Individual: all / by journey / specific members (like general notifications).
  if (scopeType === AUTOMATION_SCOPE_TYPES.INDIVIDUAL) {
    payload.scopeMember = shaped.scopeMember || null;
    const audience = shaped.scopeMember?.audienceType || "members";
    if (audience === "tracker") {
      payload.trackerId = String(shaped.trackerId || "");
      payload.trackerName = shaped.trackerName || "";
    }
  }

  return payload;
};

/**
 * Dynamic content for a tracker's workflows.
 * Standard fields + every custom question/field on the tracker (Journey Responses).
 * `tracker` is the API-loaded tracker definition.
 */
export const getDynamicFieldsForTracker = (tracker) => {
  const standard = STANDARD_DYNAMIC_FIELDS.map((item) => ({ ...item }));
  if (!tracker) return standard;

  const byId = new Map();
  getQuestionLibrary().forEach((q) => {
    if (q?.id) byId.set(q.id, q);
    if (q?.libraryKey && !byId.has(q.libraryKey)) byId.set(q.libraryKey, q);
  });
  const seen = new Set(standard.map((f) => f.token));
  const journeyResponses = [];

  const pushQuestion = (questionLike) => {
    if (!questionLike) return;
    const lib = questionLike.questionId
      ? byId.get(questionLike.questionId)
      : null;
    const label =
      questionLike.label ||
      lib?.label ||
      questionLike.questionId ||
      "Untitled";
    let token = slugToken(label, questionLike.questionId || questionLike.id);
    if (seen.has(token)) {
      token = `${token}_${String(questionLike.id || questionLike.questionId || "")
        .slice(-6)
        .replace(/[^a-z0-9]/gi, "")}`;
    }
    if (seen.has(token)) return;
    seen.add(token);
    journeyResponses.push({
      token,
      label,
      group: "Journey Responses",
      questionId: questionLike.questionId || questionLike.id || "",
    });
  };

  (tracker.personalInfoQuestions || []).forEach(pushQuestion);
  (tracker.sharedQuestions || []).forEach(pushQuestion);
  (tracker.forms || []).forEach((form) => {
    (form.questions || []).forEach(pushQuestion);
  });
  (tracker.questions || []).forEach(pushQuestion);
  (tracker.fields || []).forEach((field) => {
    pushQuestion({
      id: field.id,
      label: field.label || field.name,
      questionId: field.id,
    });
  });

  return [...standard, ...journeyResponses];
};

export const groupDynamicFields = (fields) => {
  const groups = {};
  (fields || []).forEach((field) => {
    const group = field.group || "Other";
    if (!groups[group]) groups[group] = [];
    groups[group].push(field);
  });
  return groups;
};

// ——— API-backed helpers (backend is the only source of truth) ———

const looksLikeMongoId = (id) =>
  Boolean(id) && /^[a-f\d]{24}$/i.test(String(id));

export const loadAutomationsFromApi = async (trackerId = "") => {
  try {
    const res = await getTrackerAutomationsApi({
      limit: 500,
      trackerId: trackerId || "",
    });
    if (res?.code === 200 && Array.isArray(res.automations)) {
      const list = res.automations.map((item) => normalizeAutomation(item));
      return { ok: true, automations: list, fromApi: true };
    }
    return {
      ok: false,
      automations: [],
      fromApi: false,
      message: res?.message || "Failed to load automations",
    };
  } catch (err) {
    return {
      ok: false,
      automations: [],
      fromApi: false,
      message: err?.message || "Failed to load automations",
    };
  }
};

export const loadAutomationByIdFromApi = async (id) => {
  if (!id || !looksLikeMongoId(id)) {
    return { ok: false, automation: null, message: "Automation not found" };
  }
  try {
    const res = await getTrackerAutomationDetailApi(id);
    if (res?.code === 200 && res.automation) {
      return {
        ok: true,
        automation: normalizeAutomation(res.automation),
        fromApi: true,
      };
    }
    return {
      ok: false,
      automation: null,
      fromApi: false,
      message: res?.message || "Automation not found",
    };
  } catch (err) {
    return {
      ok: false,
      automation: null,
      fromApi: false,
      message: err?.message || "Failed to load automation",
    };
  }
};

export const saveAutomationViaApi = async (automation) => {
  const payload = toAutomationApiPayload(automation);
  if (
    payload.scopeType === AUTOMATION_SCOPE_TYPES.TAGS &&
    !(payload.scopeTags || []).length
  ) {
    throw new Error("Automation requires at least one tag");
  }
  // Tracker only required for journey scope, or individual → by journey.
  // Individual → all / specific members intentionally have no trackerId.
  const individualAudience = payload.scopeMember?.audienceType || "";
  const needsTracker =
    payload.scopeType === AUTOMATION_SCOPE_TYPES.TRACKER ||
    (payload.scopeType === AUTOMATION_SCOPE_TYPES.INDIVIDUAL &&
      individualAudience === "tracker");
  if (needsTracker && !payload.trackerId) {
    throw new Error("Automation requires a tracker");
  }
  if (
    payload.scopeType === AUTOMATION_SCOPE_TYPES.INDIVIDUAL &&
    !payload.scopeMember
  ) {
    throw new Error("Choose who this automation is for");
  }

  const existingId = String(automation?.id || automation?._id || "");
  const isUpdate = looksLikeMongoId(existingId);

  const res = isUpdate
    ? await updateTrackerAutomationApi(existingId, payload)
    : await addTrackerAutomationApi(payload);

  if (res?.code !== 200 || !res.automation) {
    throw new Error(res?.message || "Could not save automation");
  }

  // Backend mirrors the link onto Tracker.automation (sync_tracker_link).
  return normalizeAutomation(res.automation);
};

export const deleteAutomationViaApi = async (id) => {
  if (!id || !looksLikeMongoId(id)) {
    throw new Error("Automation not found");
  }
  const res = await deleteTrackerAutomationApi(id);
  if (res?.code !== 200) {
    throw new Error(res?.message || "Could not delete automation");
  }
  return true;
};

export const saveWorkflowOnAutomationViaApi = async (
  automationId,
  workflow
) => {
  const parentResult = await loadAutomationByIdFromApi(automationId);
  const parent = parentResult.automation;
  if (!parent) throw new Error(parentResult.message || "Automation not found");

  const nextWorkflow = normalizeWorkflow({
    ...workflow,
    updatedAt: now(),
  });
  const workflows = [...(parent.workflows || [])];
  const index = workflows.findIndex((item) => item.id === nextWorkflow.id);
  if (index >= 0) workflows[index] = { ...workflows[index], ...nextWorkflow };
  else workflows.unshift(nextWorkflow);

  return saveAutomationViaApi({ ...parent, workflows });
};

export const deleteWorkflowFromAutomationViaApi = async (
  automationId,
  workflowId
) => {
  const parentResult = await loadAutomationByIdFromApi(automationId);
  const parent = parentResult.automation;
  if (!parent) return null;
  return saveAutomationViaApi({
    ...parent,
    workflows: (parent.workflows || []).filter(
      (item) => item.id !== workflowId
    ),
  });
};

export const applyTrackerAutomationSettingsViaApi = async (
  trackerId,
  settings = {},
  tracker = null
) => {
  if (!trackerId) return null;
  const loaded = await loadAutomationsFromApi(trackerId);
  if (!loaded.ok) {
    throw new Error(loaded.message || "Failed to load automations");
  }
  const parent =
    (loaded.automations || []).find((a) => a.trackerId === trackerId) ||
    createEmptyAutomation(tracker || { id: trackerId });
  return saveAutomationViaApi({
    ...parent,
    enabled: settings.enabled !== false,
    recipients: settings.recipients || parent.recipients,
    notifyUnassignedCoach:
      settings.notifyUnassignedCoach !== undefined
        ? settings.notifyUnassignedCoach
        : parent.notifyUnassignedCoach,
    events: normalizeAutomationEvents(settings.events || parent.events),
  });
};
